import { createFileRoute } from "@tanstack/react-router";
import { useSuspenseQuery } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { suspenseOptions } from "@/lib/suspense-query";
import { getGetOrderQueryOptions } from "@/requests/gen/react-query/order";
import OrderDetailsCard from "./-details-card";
import OrderConfirmCard from "./-confirm-card";
import OrderContentsCard from "./-contents-card";
import OrderHistory from "./-order-history";
import OrderNoteForm from "./-order-notes-form";
import CancelRefundActions from "./-cancel-refund-actions";
import { OrderCrumbs } from "../-crumbs";
import TasksCard from "./-tasks-card";
import { RequirePermission } from "@/components/universal/require-permission";
import { PermissionEnum } from "@/requests/gen/react-query/fastAPI.schemas";

export const Route = createFileRoute("/_private/_patient-360/order/$orderId/")({
  loader: ({ context: { queryClient }, params: { orderId } }) =>
    queryClient.ensureQueryData(getGetOrderQueryOptions(orderId)),
  component: OrderReviewPage,
});

function OrderReviewPage() {
  const { orderId } = Route.useParams();
  // Fetch the order data
  const order = useSuspenseQuery(
    suspenseOptions(getGetOrderQueryOptions(orderId)),
  );

  const isCancelled = order.data?.status === "Cancelled";

  const canEdit =
    !order.data?.suborders.some((suborder) => suborder.charged_at !== null) &&
    !isCancelled;

  return (
    <div>
      <title>{`Review Order ${orderId} - Legrande Health Portal`}</title>
      <OrderCrumbs canEdit={canEdit}>
        {order.data && (
          <CancelRefundActions
            canEdit={canEdit}
            order={order.data}
            refetch={order.refetch}
          />
        )}
      </OrderCrumbs>

      {/* Main Content */}
      <main className="grid flex-1 items-start gap-4 xl:grid-cols-3">
        {/* Big Column */}
        <div className="grid gap-4 xl:col-span-2">
          <div className="relative">
            {/* Contents Section */}
            <OrderContentsCard
              order={order.data}
              canEdit={canEdit}
              refetch={order.refetch}
            />
            {isCancelled && (
              <div className="absolute inset-0 bg-order-cancelled z-50 rounded-lg" />
            )}
          </div>

          {/* Task Section */}
          <RequirePermission permission={PermissionEnum.filter_bypass}>
            <div className="relative">
              <TasksCard order={order.data} refetch={order.refetch} />
              {isCancelled && (
                <div className="absolute inset-0 bg-order-cancelled z-50 rounded-lg" />
              )}
            </div>
          </RequirePermission>

          {/* History Section */}
          <Card className="gap-2">
            <CardHeader>
              <CardTitle className="text-base">History</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <OrderHistory order={order.data} />
              <OrderNoteForm order={order.data} onSuccess={order.refetch} />
            </CardContent>
          </Card>
        </div>

        {/* Small Column */}
        <div className="grid gap-4">
          {/* Details Section */}
          <OrderDetailsCard
            order={order.data}
            canEdit={canEdit}
            refetch={order.refetch}
          />

          {/* Confirm Section */}
          <div className="relative">
            {order.data && (
              <OrderConfirmCard order={order.data} refetch={order.refetch} />
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
